<template>
  <v-container fluid>
    <v-container fluid>
      <v-tabs v-model="activeTab">
        <v-tab key="by-room">By Service</v-tab>
        <v-tab key="by-participant">By Participant</v-tab>
        <v-tab-item key="by-room">
          <v-container fluid>
            <rooming-list-by-room
              :items="items"
              :participants="participants"
              :assignments="assignments"
              @update:assignments="updateAssignments"
            ></rooming-list-by-room>
          </v-container>
        </v-tab-item>
        <v-tab-item key="by-participant">
          <v-container fluid>
            <rooming-list-by-participant
              :items="items"
              :participants="participants"
              :assignments="assignments"
              @update:assignments="updateAssignments"
              @update:groupLabel="updateGroupLabel"
            ></rooming-list-by-participant>
          </v-container>
        </v-tab-item>
      </v-tabs>
    </v-container>
    <v-row justify="center" class="mt-4">
      <download-csv :data="csvData" delimiter=",">
        <v-btn small class="mr-1"
          ><v-icon left small>mdi-file-delimited</v-icon> Get CSV</v-btn
        >
      </download-csv>
      <v-btn
        small
        color="primary"
        @click="saveAssignments"
        :loading="saving"
        :disabled="saving"
      >
        <v-icon left small>mdi-content-save</v-icon> Save
      </v-btn>
    </v-row>
    <v-row v-if="debug">
      <pre>
        {{ csvData }}
      </pre>
    </v-row>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import RoomingListByRoom from "./RoomingListByRoom.vue";
import RoomingListByParticipant from "./RoomingListByParticipant.vue";
const UPDATE_IITNERARY_PARTICIPANT_MUTATION = gql`
  mutation updateItineraryParticipants(
    $id: ID!
    $participants: [ItineraryParticipantInput]!
  ) {
    updateItineraryParticipants(
      id: $id
      itineraryParticipantInputs: $participants
    ) {
      message
      error
    }
  }
`;
export default {
  name: "RoomingList",
  components: {
    RoomingListByRoom,
    RoomingListByParticipant,
  },
  props: {
    itinerary: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      debug: false,
      activeTab: 0,
      assignments: {}, // Map of participantId -> [serviceIds]
      groupLabels: {}, // Map of participantId -> groupLabel
      saving: false,
    };
  },
  computed: {
    participantsByGroupLabel() {
      return this.participants.reduce((acc, participant) => {
        acc[participant.groupLabel] = [
          ...(acc[participant.groupLabel] || []),
          participant,
        ];
        return acc;
      }, {});
    },
    csvData() {
      return this.items
        .filter((item) => item.serviceTypeName != "MANDATORY_CHARGES")
        .reduce((acc, item) => {
          const assignedParticipants = this.getAssignedParticipants(item.id);
          const assigned = assignedParticipants.length;
          const remaining =
            parseInt(item.pax.adults) +
            parseInt(item.pax.children) +
            parseInt(item.pax.infants) -
            assigned;
          const assignedGroupLabels = assignedParticipants.map(
            (p) => p.groupLabel
          );
          const isAccommodation = item.serviceTypeName == "ACCOMMODATION";
          const row = {
            "Service Date": item.startDate,
            Supplier: item.supplierName,
            Service: item.serviceName,
            Adults: item.pax.adults,
            Children: item.pax.children,
            Infants: item.pax.infants,
            Assigned: assigned,
            Remaining: remaining,
            "Assigned Pax": isAccommodation
              ? ""
              : assignedParticipants
                  .map((p) => `${p.name} ${p.surname}`)
                  .join(", "),
            "Group Label": isAccommodation ? "" : "",
          };
          acc.push(row);
          if (isAccommodation) {
            for (const groupLabel of new Set(assignedGroupLabels)) {
              const accRow = {
                "Service Date": "",
                Supplier: "",
                Service: "",
                Adults: "",
                Children: "",
                Infants: "",
                Assigned: "",
                Remaining: "",
                "Assigned Pax": "",
                "Group Label": "",
              };

              accRow["Group Label"] = groupLabel;
              accRow["Assigned Pax"] = assignedParticipants
                .filter((p) => p.groupLabel == groupLabel)
                .map((p) => `${p.name} ${p.surname}`)
                .join(", ");
              acc.push(accRow);
            }
          }
          return acc;
        }, []);
    },
    items() {
      return this.itinerary.items && this.itinerary.items.edges
        ? this.itinerary.items.edges.map((e) => e.node)
        : [];
    },
    participants() {
      return this.itinerary.participants && this.itinerary.participants.edges
        ? this.itinerary.participants.edges.map((e) => ({
            ...e.node,
            id: e.node.number, // Use number as id for assignments (child components expect id)
            groupLabel:
              this.groupLabels[e.node.number] !== undefined
                ? this.groupLabels[e.node.number]
                : e.node.groupLabel || "", // Use tracked groupLabel or fallback to original
          }))
        : [];
    },
  },
  watch: {
    "itinerary.participants.edges": {
      immediate: true,
      handler(newEdges, oldEdges) {
        // Only initialize when participants data actually changes from the API
        // This avoids re-initializing when local state (groupLabels) changes
        if (newEdges && newEdges !== oldEdges) {
          this.initializeAssignments();
        }
      },
    },
  },
  methods: {
    getAssignedParticipants(serviceId) {
      return this.participants.filter((p) => {
        const participantServices = this.assignments[p.id] || [];
        return participantServices.includes(serviceId);
      });
    },
    initializeAssignments() {
      console.log("initializeAssignments", this.participants);
      // Initialize assignments from participant.rooms field
      const newAssignments = {};
      const newGroupLabels = {};
      const participants =
        this.itinerary.participants && this.itinerary.participants.edges
          ? this.itinerary.participants.edges.map((e) => e.node)
          : [];

      participants.forEach((participant) => {
        const participantId = participant.number;
        // participant.rooms should be an array of service/itineraryItem IDs
        if (participant.rooms && Array.isArray(participant.rooms)) {
          newAssignments[participantId] = [...participant.rooms];
        } else {
          newAssignments[participantId] = [];
        }
        // Initialize groupLabel from participant data
        if (participant.groupLabel !== undefined) {
          newGroupLabels[participantId] = participant.groupLabel || "";
        }
      });
      this.assignments = newAssignments;
      this.groupLabels = { ...this.groupLabels, ...newGroupLabels };
    },
    onExportCSV() {
      // Implement CSV export logic here
      console.log(this.activeTab);
      console.log("Export CSV");
    },
    updateAssignments(newAssignments) {
      this.assignments = { ...newAssignments };
    },
    updateGroupLabel({ participantId, groupLabel }) {
      // Update the groupLabel in the tracked data
      this.groupLabels = {
        ...this.groupLabels,
        [participantId]: groupLabel || "",
      };
    },
    async saveAssignments() {
      this.saving = true;
      try {
        // Convert assignments back to participant format for saving
        const participantsToUpdate = this.participants.map((participant) => {
          return {
            title: participant.title,
            name: participant.name,
            surname: participant.surname,
            email: participant.email,
            mobile: participant.mobile,
            telephone: participant.telephone,
            rooms: this.assignments[participant.id] || [],
            groupLabel:
              this.groupLabels[participant.id] !== undefined
                ? this.groupLabels[participant.id]
                : participant.groupLabel || "",
          };
        });

        const result = await this.$apollo.mutate({
          mutation: UPDATE_IITNERARY_PARTICIPANT_MUTATION,
          variables: {
            id: this.itinerary.id,
            participants: participantsToUpdate,
          },
        });

        if (result.data.updateItineraryParticipants.error) {
          this.$store.dispatch("showAppSnackbar", {
            msg: result.data.updateItineraryParticipants.error,
            color: "error",
          });
        } else {
          this.$store.dispatch("showAppSnackbar", {
            msg:
              result.data.updateItineraryParticipants.message ||
              "Room assignments saved successfully.",
          });
          // Refetch itinerary data to get updated participants
          if (this.$apollo.queries.itinerary) {
            this.$apollo.queries.itinerary.refetch();
          }
        }
      } catch (error) {
        console.error("Error saving assignments:", error);
        const errorMsg = error.graphQLErrors
          ? error.graphQLErrors.map((e) => e.message).join(", ")
          : error.message || "Failed to save room assignments";
        this.$store.dispatch("showAppSnackbar", {
          msg: errorMsg,
          color: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/rooming-list/RoomingList.vue